import { disposeHierarchy } from './../../utils/threejs'
import { GameObject3D } from './../../e.elements/GameElements/classes/types'
import create, { StateCreator } from 'zustand'
import { devtools } from 'zustand/middleware'
import { Object3D } from 'three'
import produce from 'immer'
import { CHARACTER_TYPES } from '../../f.assets/Characters/scripts/constants'
import { useSceneStore } from '../../a.scenes/GameScene/store'

// store array reference at module level to avoid Redux to serialize many objects and crash
let gameElements = new Map<string, Object3D>()

const SceneInitialState = {
  gameElementsUUID: [],
  user: {
    name: '',
    candySummary: 0,
    currentLevelId: '',
    team: undefined,
  },
}
export type SceneSlice = {
  user: typeof SceneInitialState.user
  gameElementsUUID: string[]
  getGameElements: () => Map<string, Object3D>
  getGameElement: (name: string) => Object3D | undefined
  getGameObject3D: (name: string) => GameObject3D | undefined
  getGameElementByUUID: (uuid: string) => Object3D | undefined
  addGameElement: (name: string, element: Object3D) => void
  clearGameElement: () => void
  updateUser: (data: Record<string, unknown>) => void
  updateCandyStock: (amount: number) => void
  setPlayerTeam: (team: CHARACTER_TYPES.JACK | CHARACTER_TYPES.WITCH) => void
}

const createSceneSlice: StateCreator<SceneSlice, [['zustand/devtools', never]], [], SceneSlice> = (set, get) => ({
  ...SceneInitialState,
  addGameElement: (name: string, element: Object3D) => {
    gameElements.set(name, element)
    set(state => ({ gameElementsUUID: [...state.gameElementsUUID, element.uuid] }), false, { type: 'addGameElement' })
  },
  // deprecated soon, use getGameObject3D
  getGameElement: (name: string) => get().getGameObject3D(name),
  getGameObject3D: (uuidOrName: string) => {
    // param can be a uuid or a name (<GameElement name="label" />)
    if (gameElements.has(uuidOrName)) {
      // uuidOrName is a uuid for an anonymous game object or a name for a labeled game object
      return gameElements.get(uuidOrName) as GameObject3D
    } else {
      // uuidOrName is a uuid for a labeled object then the key into elements map is the label
      const element = Array.from(gameElements.values()).find((obj: Object3D) => obj.uuid === uuidOrName)
      if (!element) {
        throw new Error(`Name or element not found for the given parameter (uuidOrName): ${uuidOrName}`)
      }
      return element as GameObject3D
    }
  },
  getGameElementByUUID: (uuid: string) => [...gameElements.values()].find((obj: Object3D) => obj.uuid === uuid),
  // @TODO should destroy elements?
  clearGameElement: () => {
    gameElements.forEach(e => {
      disposeHierarchy(e)
    })
    gameElements.clear()
    set({ gameElementsUUID: [] }, false, { type: 'clearGameElement' })
  },
  getGameElements: () => gameElements,
  updateUser: (data: Record<string, unknown>) =>
    set(
      produce(state => {
        state.user = { ...state.user, ...data }
      }),
      false,
      { type: 'updateUser' },
    ),
  updateCandyStock: (amount: number) =>
    set(
      produce(state => {
        state.user.candySummary += amount
      }),
      false,
      { type: 'updateCandyStock' },
    ),
  setPlayerTeam: (team: CHARACTER_TYPES.JACK | CHARACTER_TYPES.WITCH) =>
    set(
      produce(state => {
        state.user.team = team
        state.user.currentLevelId = 'level1'
        // const players = useSceneStore.getState().levels.data.players
        // const players = state.levels.data.players
        // const comTeam = team === CHARACTER_TYPES.JACK ? CHARACTER_TYPES.WITCH : CHARACTER_TYPES.JACK
        // players.forEach((p: Player) => {
        //   p.team = p.id === COMMANDERS.PLAYER ? team : comTeam
        // })
      }),
      false,
      { type: 'setPlayerTeam' },
    ),
})

export const useScenesStore = create<SceneSlice>()(
  devtools((...a) => ({
    ...createSceneSlice(...a),
  })),
)
